// horizontal-marquee.js
import gsap from "gsap";

export default class HorizontalMarquee {
    /**
     * Options:
     * - marqueeSelector   : sélecteur des carousels legacy existants (string)
     * - sectionSelectors  : liste de sections à hydrater en marquee (array<string>)
     * - sectionTrackSel   : sélecteur du "track source" dans chaque section hydratée (string)
     * - speed             : px/s (peut être override via data-marquee-speed)
     * - direction         : 1 = gauche, -1 = droite (override via data-direction)
     * - alternateDirections: alterne LTR/RTL par ligne (utile si plusieurs lignes)
     * - pauseOnHover      : pause au hover/focus
     * - autoInitOnLoad    : init sur window.load
     */
    constructor(options = {}) {
        this.opts = {
            marqueeSelector    : options.marqueeSelector ?? ".marquee",
            sectionSelectors   : Array.isArray(options.sectionSelectors) ? options.sectionSelectors : (options.sectionSelectors ? [options.sectionSelectors] : []),
            sectionTrackSel    : options.sectionTrackSel ?? ".grid_content",
            speed              : options.speed ?? 60,     // px/s pour parcourir 1 set de base
            direction          : options.direction ?? 1,  // 1 = gauche, -1 = droite
            alternateDirections: options.alternateDirections ?? false,
            pauseOnHover       : options.pauseOnHover ?? true,
            autoInitOnLoad     : options.autoInitOnLoad ?? true,
        };

        this.roots = [];
        // 1) Legacy .marquee déjà en place
        document.querySelectorAll(this.opts.marqueeSelector).forEach(el => {
            this.roots.push({ el, mode: "legacy" });
        });
        // 2) Sections à hydrater
        this.opts.sectionSelectors.forEach(sel => {
            document.querySelectorAll(sel).forEach(el => {
                this.roots.push({ el, mode: "hydrate" });
            });
        });

        if (!this.roots.length) return;

        if (this.opts.autoInitOnLoad) {
            window.addEventListener("load", () => this.init());
        } else {
            this.init();
        }
    }

    init() {
        const prefersReduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

        this.roots.forEach((rootObj, rootIndex) => {
            const { el, mode } = rootObj;

            // A) structure .marquee -> wrapper -> track
            const marquee = mode === "legacy" ? el : this.hydrateToMarquee(el);
            if (!marquee) return;

            const wrapper = marquee.querySelector(".marquee-wrapper");
            const track   = marquee.querySelector(".marquee-track");
            if (!wrapper || !track) return;

            // B) options par élément
            const ds = marquee.dataset || {};
            const speedAttr = ds.marqueeSpeed ? parseFloat(ds.marqueeSpeed) : null;
            const speed     = Number.isFinite(speedAttr) ? speedAttr : this.opts.speed; // px/s pour 1 set
            const dirAttr   = (ds.direction ?? ds.marqueeDirection);
            const direction = (this.opts.alternateDirections ? (rootIndex % 2 ? -1 : 1) : this.opts.direction) * (dirAttr ? parseInt(dirAttr, 10) : 1);

            // C) items de base (set d’origine)
            let baseItems = Array.from(track.children);
            if (!baseItems.length) return;
            baseItems.forEach(n => n.classList.add("marquee-item"));

            // D) styles safe
            marquee.style.overflow = marquee.style.overflow || "hidden";
            wrapper.style.overflow = wrapper.style.overflow || "hidden";
            track.style.display = "flex";
            track.style.flexWrap = "nowrap";
            track.style.willChange = "transform";

            // E) état
            let tickerOn = false;
            let paused = false;

            // ===== Helpers locaux à init() — MODE RECYCLER =====

            // 1) supprime tous les clones
            const purgeClones = () => {
                track.querySelectorAll("[data-ghost='true']").forEach(n => n.remove());
            };

            // 2) largeur d’un set (somme des N premiers items)
            const measureRowWidth = () => {
                const firstRow = Array.from(track.children).slice(0, baseItems.length);
                return firstRow.reduce((acc, el) => acc + el.getBoundingClientRect().width, 0);
            };

            // 3) gap horizontal effectif (flex)
            const getGap = () => {
                const cs = getComputedStyle(track);
                return parseFloat(cs.columnGap || cs.gap || 0) || 0;
            };

            // 4) normaliser : repartir d’UN set propre (zéro ghost)
            const normalizeToOneSet = () => {
                purgeClones();
                if (track.children.length > baseItems.length) {
                    const keep = Array.from(track.children).slice(0, baseItems.length);
                    track.innerHTML = "";
                    keep.forEach(n => track.appendChild(n));
                }
            };

            // 5) seed minimal : ajouter quelques items (pas des sets) pour couvrir la largeur visible
            const seedMinimalBuffer = () => {
                const wrapperW = wrapper.getBoundingClientRect().width || marquee.getBoundingClientRect().width || window.innerWidth;
                let currentW = track.getBoundingClientRect().width;
                let i = 0;

                while (currentW < wrapperW + 200 /* marge confort */ && i < baseItems.length * 2) {
                    const c = track.children[i % baseItems.length].cloneNode(true);
                    c.dataset.ghost = "true";
                    c.setAttribute("aria-hidden", "true");
                    c.style.pointerEvents = "none";
                    track.appendChild(c);
                    currentW = track.getBoundingClientRect().width;
                    i++;
                }
            };

            // 6) animation anti-jump : ticker + recyclage 1er/dernier item
            const startTicker = (rowWidth) => {
                gsap.set(track, { x: 0, clearProps: "transform" });
                let x = 0;

                if (prefersReduce) return; // no anim si reduced motion

                const gap = getGap();
                const pxPerSec = Math.max(1, rowWidth) / Math.max(1, speed); // vitesse linéaire
                const dpr = window.devicePixelRatio || 1;
                const snap = (v) => Math.round(v * dpr) / dpr;
                const setX = gsap.quickSetter(track, "x", "px");

                const tick = (time, delta) => {
                    if (paused) return;

                    x -= (delta / 1000) * pxPerSec * direction;

                    // recycle : quand le 1er item sort (ou le dernier si direction -1)
                    if (direction === 1) {
                        let first = track.firstElementChild;
                        while (first) {
                            const w = first.getBoundingClientRect().width + gap;
                            if (x <= -w) {
                                x += w;
                                track.appendChild(first);
                                first = track.firstElementChild;
                                continue;
                            }
                            break;
                        }
                    } else {
                        // RTL : on recycle par l’arrière
                        let last = track.lastElementChild;
                        while (last) {
                            const w = last.getBoundingClientRect().width + gap;
                            if (x >= w) {
                                x -= w;
                                track.insertBefore(last, track.firstElementChild);
                                last = track.lastElementChild;
                                continue;
                            }
                            break;
                        }
                    }

                    setX(snap(x));
                };

                if (!tickerOn) {
                    gsap.ticker.add(tick);
                    tickerOn = true;

                    // pause au hover/focus
                    if (this.opts.pauseOnHover) {
                        wrapper.addEventListener("mouseenter", () => { paused = true; }, { passive: true });
                        wrapper.addEventListener("mouseleave", () => { paused = false; }, { passive: true });
                        wrapper.addEventListener("focusin",   () => { paused = true; });
                        wrapper.addEventListener("focusout",  () => { paused = false; });
                    }

                    // pause onglet caché
                    const onVis = () => { if (document.hidden) paused = true; };
                    document.addEventListener("visibilitychange", onVis);

                    // cleanup via l’event existant
                    marquee.addEventListener("marquee:destroy", () => {
                        gsap.ticker.remove(tick);
                        document.removeEventListener("visibilitychange", onVis);
                        tickerOn = false;
                    }, { once: true });
                }
            };

            // 7) refresh (appelé au start + au resize)
            const refresh = () => {
                normalizeToOneSet();        // repartir d'un set propre
                const rowW = measureRowWidth(); // largeur du set
                seedMinimalBuffer();        // ajouter juste le buffer nécessaire
                startTicker(rowW);          // animer en recyclant => zéro jump, DOM min
            };

            // Pause si reduced motion
            if (prefersReduce) {
                // rien, pas d’anim
            }

            // Resize observer + orientation
            const ro = new ResizeObserver(() => refresh());
            ro.observe(wrapper);
            ro.observe(track);
            window.addEventListener("orientationchange", refresh);

            // Start
            // (attendre un layout stable — images souvent décodées à ce stade si width/height renseignés)
            refresh();

            // Cleanup si SPA
            marquee.addEventListener("marquee:destroy", () => {
                ro.disconnect();
                window.removeEventListener("orientationchange", refresh);
                // le ticker est nettoyé dans startTicker via l’event destroy
                // purge éventuelle des clones
                track.querySelectorAll("[data-ghost='true']").forEach(n => n.remove());
                gsap.set(track, { clearProps: "all" });
            }, { once: true });
        });

        this.roots.forEach(({ el }) => {
            const m = el.tagName === 'DIV' && el.classList.contains('marquee')
                ? el
                : el.querySelector('.marquee');
            if(m) m.classList.add('is-ready');
        });
    }

    /**
     * Transforme une section custom en :
     * <div class="marquee">
     *   <div class="marquee-wrapper">
     *     <div class="marquee-track"> ...children... </div>
     *   </div>
     * </div>
     */
    hydrateToMarquee(section) {
        const source = section.querySelector(this.opts.sectionTrackSel);
        if (!source) return null;

        const marquee = document.createElement("div");
        marquee.className = "marquee";

        const wrapper = document.createElement("div");
        wrapper.className = "marquee-wrapper";

        const track = document.createElement("div");
        track.className = "marquee-track";

        // déplacer les enfants existants dans le track
        Array.from(source.children).forEach(node => track.appendChild(node));

        wrapper.appendChild(track);
        marquee.appendChild(wrapper);

        source.replaceWith(marquee);
        return marquee;
    }
}
